/**
 * web/src/shell/nav.ts — destination navigation and the More/compose pick
 * handlers, moved verbatim out of App.tsx (card WUHnArc6lNID). One hook owns
 * every navigateTo-shaped callback; it declares no effects, so App may call
 * it wherever it reads naturally.
 */
import { useCallback, useState, type Dispatch, type SetStateAction } from 'react';
import type { MorePick } from '../components/MoreMenu';
import type { ComposeTile } from '../components/ComposeMenu';
import { COMPOSE_ROUTE, destHash, type ComposeIntent, type Dest, type Tab } from './dest';

export function useAppNav(
  { setTab, setSettingsOpen, setShowingAI, setExtraView, setUsTab, setLifeTab, setMoreOpen, setTourOpen, setUsPrefsSignal, setThreadFilter, setNotebookCollection }: {
    setTab: Dispatch<SetStateAction<Tab>>;
    setSettingsOpen: Dispatch<SetStateAction<boolean>>;
    setShowingAI: Dispatch<SetStateAction<boolean>>;
    setExtraView: Dispatch<SetStateAction<'help' | null>>;
    setUsTab: Dispatch<SetStateAction<string>>;
    setLifeTab: Dispatch<SetStateAction<string>>;
    setMoreOpen: Dispatch<SetStateAction<boolean>>;
    setTourOpen: Dispatch<SetStateAction<boolean>>;
    setUsPrefsSignal: Dispatch<SetStateAction<number>>;
    setThreadFilter: Dispatch<SetStateAction<'all' | 'chat' | null>>;
    setNotebookCollection: Dispatch<SetStateAction<string | null>>;
  },
) {
  /** One-shot navigation to an explicit destination (card Mn55eOvFgvC0, H4).
   *  Closes every overlay, picks the section AND its subsection, carries an
   *  optional entity id, and pushes the destination onto the URL hash so the
   *  browser Back button retraces screen changes instead of leaving the app. */
  const navigateTo = useCallback((dest: Dest) => {
    setSettingsOpen(false);
    setShowingAI(false);
    setExtraView(null);
    if (dest.subsection) {
      if (dest.section === 'us') setUsTab(dest.subsection);
      if (dest.section === 'life') setLifeTab(dest.subsection);
    }
    setTab(dest.section);
    const hash = destHash(dest);
    if (window.location.hash !== hash) {
      window.history.pushState(null, '', hash);
    }
  }, []);
  /** A More-menu pick: close the menu, land on the target, then do whatever
   *  the quick link promised (thread filter, notebook collection, scroll). */
  const handleMorePick = useCallback((pick: MorePick) => {
    setMoreOpen(false);
    if (pick.tour) {
      setTourOpen(true);
      return;
    }
    if (pick.settings) {
      setShowingAI(false);
      setExtraView(null);
      setSettingsOpen(true);
      return;
    }
    // Phase-0 screens (card YMMXiFOdFgdF) are panels over the tabs, not
    // tabs — they take the slot Settings takes, then get out of the way.
    if (pick.view) {
      setSettingsOpen(false);
      setShowingAI(false);
      setExtraView(pick.view);
      return;
    }
    // Route through navigateTo so a More pick lands on its named subsection
    // and joins the Back history like every other deep link.
    if (pick.tab) {
      navigateTo({
        section: pick.tab,
        subsection: pick.lifeSub ?? pick.usSub ?? undefined,
      });
    }
    // Card GMat5z0Zxutf: "Date preferences" jumps straight into preferences
    // once the Dates tab has mounted — bump the signal the panel listens for.
    if (pick.usPrefs) setUsPrefsSignal((n) => n + 1);
    if (pick.threadFilter) setThreadFilter(pick.threadFilter);
    if (pick.collection) setNotebookCollection(pick.collection);
    if (pick.scrollTo) {
      // The panel mounts on the same tick as the tab switch — wait a frame
      // so the target exists before we scroll it into view.
      window.setTimeout(() => {
        document.getElementById(pick.scrollTo!)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 120);
    }
  }, [navigateTo]);
  /** Compose-menu routing (integration wire): each tile picks a target tab
   *  AND carries a ComposeIntent so the destination panel opens its own
   *  composer (design v1.1 §5.2 — the + menu is feature discovery, so a tap
   *  lands on the actual action, not just a tab switch). */
  const [composeIntent, setComposeIntent] = useState<ComposeIntent>(null);

  /* One-shot intent (card 4vIN4UouHPz4): a deep-link intent must not outlive
     the screen that acts on it. Thread mounts only while its tab is active, so
     before this the intent survived every unmount and its mount effect
     re-fired on each return — the meeting dialog and the kudos sender
     resurrected themselves on unrelated screens after first use. */
  const clearComposeIntent = useCallback(() => setComposeIntent(null), []);

  const handleComposePick = useCallback((tile: ComposeTile) => {
    if (tile === 'message') {
      setComposeIntent(null);
      return; // stays in the thread; Thread refocuses its own input
    }
    const route = COMPOSE_ROUTE[tile];
    // Route through navigateTo so the compose tile lands on its named
    // subsection and joins the Back history (card Mn55eOvFgvC0).
    navigateTo({ section: route.tab, subsection: route.subsection });
    setComposeIntent(route.intent);
  }, [navigateTo]);

  /** Card BNw401SBOLFO (IA phase 3): the Dates panel's "+ Idea" door. A date
   *  idea is authored in exactly one place — the Notebook capture bar — so the
   *  door is a compose-intent route, not a second composer: Notebook mounts
   *  with kind=date, opens the bar on the date chip and focuses it. */
  const addDateIdeaInNotebook = useCallback(() => {
    navigateTo({ section: 'notebook' });
    setComposeIntent({ kind: 'date' });
  }, [navigateTo]);

  /** Card anKalYfYvokw (IA phase 2): the Notebook date footnote names the
   *  route to Us → Dates, so it opens it. Plain navigation — no compose intent,
   *  because the person is going to plan, not to author. */
  const openDatesFromNotebook = useCallback(() => {
    navigateTo({ section: 'us', subsection: 'dates' });
  }, [navigateTo]);

  /** Gift flow (card NYJEJRCBfBGQ): "My taste, kept in Us" deep-links to the
   *  Us profile — the flow never edits taste itself, it routes there. */
  const openUsFromNotebook = useCallback(() => {
    navigateTo({ section: 'us' });
  }, [navigateTo]);

  /** A first-act pick (card Tw2G4BUmOF5o): land on the named destination with
   *  its own composer already open, so one tap ends on the action. */
  const goFirstAct = useCallback(
    (dest: Dest, intent: ComposeIntent = null) => {
      navigateTo(dest);
      setComposeIntent(intent);
    },
    [navigateTo],
  );
  return {
    navigateTo,
    composeIntent,
    clearComposeIntent,
    handleComposePick,
    handleMorePick,
    addDateIdeaInNotebook,
    openDatesFromNotebook,
    openUsFromNotebook,
    goFirstAct,
  };
}
